
import { Box, Button, Typography, TextField, Grid } from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import rmMasterService from "../../../services/masters/rmMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

// ── Styles ────────────────────────────────────────────────────────────────────

const pageSx = {
    background: "#F4F7FB",
    minHeight: "100vh",
    padding: "20px 24px 40px",
    fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
    fontSize: "12px",
    color: "#718096",
    mb: 2,
    display: "flex",
    alignItems: "center",
    gap: "6px",
};

const pageHeaderSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2.5,
};

const pageTitleSx = {
    fontSize: "16px",
    fontWeight: 700,
    color: "#000000",
    display: "flex",
    alignItems: "center",
    gap: "10px",
};

const backBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#2B6CB0",
    padding: "7px 14px",
    border: "1.5px solid #2B6CB0",
    borderRadius: "7px",
    background: "#fff",
    cursor: "pointer",
    textTransform: "none",
    "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
    background: "#fff",
   
    border: "1px solid #E2E8F0",
    overflow: "hidden",
};

const sectionHeaderSx = {
    background: "#1C3A5E",
    padding: "12px 22px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
};

const sectionHeaderTitleSx = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
    fontSize: "12px",
    fontWeight: 600,
    color: "#2063b6",
    mb: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
};

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        fontSize: "13px",
        fontFamily: "'Inter', Arial, sans-serif",
        background: "#fafafa",
        height: "38px",
        "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
        "&:hover fieldset": { borderColor: "#CBD5E0" },
        "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
        "&.Mui-focused": { background: "#fff" },
        "&.Mui-disabled": { background: "#F8FAFC" },
    },
    "& input": {
        color: "#1A202C",
        fontSize: "13px",
        fontFamily: "'Inter', Arial, sans-serif",
        "&::placeholder": { color: "#A0AEC0", opacity: 1 },
    },
    "& .MuiInputLabel-root": { display: "none" },
};

const formFooterSx = {
    padding: "18px 24px",
    background: "#F8FAFC",
    borderTop: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
};

const saveBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 22px",
    height: "40px",
    background: "#4CAF50",
    color: "#fff",
    border: "none",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
    "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
    transition: "all 0.15s ease",
};

const cancelBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 22px",
    height: "40px",
    background: "#E53E3E",
    color: "#ffffff",
    border: "none",
    textTransform: "none",
    "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
    fontSize: "14px",
    fontWeight: 600,
    borderRadius: "8px",
};

// ─────────────────────────────────────────────────────────────────────────────

const RMAdd = () => {
    const { handleSubmit } = useForm();
    const navigate = useNavigate();
    const urlLocation = useLocation();
    const editData = urlLocation?.state;

    const login_details = sessionStorage.getItem('loginUser');
    const userD = JSON.parse(login_details);
    const userInfo = userD.result.userInfo;

    const [data, setData] = useState({
        rm_id: '',
        rm_code: '',
        rm_name: '',
        uom_consumption: '',
        uom_purchase: '',
        rate: '',
        opening_bal: '',
        min_bal: '',
        max_bal: '',
        bin_no: '',
    });

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Prefill if editing
    useEffect(() => {
        if (editData) {
            setData({
                rm_id: editData.rm_id || '',
                rm_code: editData.rm_code || '',
                rm_name: editData.rm_name || '',
                uom_consumption: editData.uom_consumption || '',
                uom_purchase: editData.uom_purchase || '',
                rate: editData.rate || '',
                opening_bal: editData.opening_bal || '',
                min_bal: editData.min_bal || '',
                max_bal: editData.max_bal || '',
                bin_no: editData.bin_no || '',
            });
        }
    }, [editData]);

    const handleChange = ({ target }) => {
        const { name, value } = target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const onSubmit = async () => {
        setErrorMessage('');

        const rmData = {
            ...data,
            modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            modified_by: editData ? userInfo.user_id : null,
            created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            created_by: editData ? editData.created_by : userInfo.user_id,
            changedUpdatedValue: 'edit',
        };

        try {
            if (editData) {
                await rmMasterService.editDeleteRM(rmData);
            } else {
                await rmMasterService.addRM(rmData);
            }
            setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
            setTimeout(() => navigate(-1), 1000);
        } catch (error) {
            console.error("Submission error:", error);
            setErrorMessage("Error occurred while saving data.");
        }
    };

    const handleBack = () => navigate(-1);

    return (
        <Box sx={pageSx}>
            {/* Breadcrumb */}
            <Box sx={breadcrumbSx}>
                <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>RM Master</span>
            </Box>

            {/* Page Header */}
            <Box sx={pageHeaderSx}>
                <Box sx={pageTitleSx}>
                    <InventoryIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
                    {editData ? 'UPDATE' : 'CREATE'} RM MASTER
                </Box>
                <Button onClick={handleBack} sx={backBtnSx}>
                    <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
                </Button>
            </Box>

            {/* Main Form Card */}
            <Box sx={formCardSx}>
                {/* Navy Strip Header */}
                <Box sx={sectionHeaderSx}>
                    <InventoryIcon sx={{ fontSize: 18, color: "#fff" }} />
                    <Typography sx={sectionHeaderTitleSx}>RM Master</Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={formBodySx}>
                        <Grid container spacing={3}>

                            {/* Row 1 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        RM Code <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="rm_code"
                                        value={data.rm_code}
                                        onChange={handleChange}
                                        placeholder="Enter RM code"
                                        required
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        RM Name <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="rm_name"
                                        value={data.rm_name}
                                        onChange={handleChange}
                                        placeholder="Enter RM name"
                                        required
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 2 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>UOM Consumption</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="uom_consumption"
                                        value={data.uom_consumption}
                                        onChange={handleChange}
                                        placeholder="Enter UOM consumption"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>UOM Purchase</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="uom_purchase"
                                        value={data.uom_purchase}
                                        onChange={handleChange}
                                        placeholder="Enter UOM purchase"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 3 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Rate</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="rate"
                                        value={data.rate}
                                        onChange={handleChange}
                                        placeholder="Enter rate"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Opening Balance</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="opening_bal"
                                        value={data.opening_bal}
                                        onChange={handleChange}
                                        placeholder="Enter opening balance"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 4 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Min Balance</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="min_bal"
                                        value={data.min_bal}
                                        onChange={handleChange}
                                        placeholder="Enter minimum balance"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Max Balance</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="max_bal"
                                        value={data.max_bal}
                                        onChange={handleChange}
                                        placeholder="Enter maximum balance"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Row 5 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Bin No</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="text"
                                        name="bin_no"
                                        value={data.bin_no}
                                        onChange={handleChange}
                                        placeholder="Enter bin number"
                                        sx={inputSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Success / Error messages */}
                            {successMessage && (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "#4CAF50", fontSize: "14px", mt: 1 }}>
                                        {successMessage}
                                    </Typography>
                                </Grid>
                            )}
                            {errorMessage && (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "#E53E3E", fontSize: "14px", mt: 1 }}>
                                        {errorMessage}
                                    </Typography>
                                </Grid>
                            )}

                        </Grid>
                    </Box>

                    {/* Form Footer */}
                    <Box sx={formFooterSx}>
                        <Button sx={cancelBtnSx} onClick={handleBack}>
                            Cancel
                        </Button>
                        <Button type="submit" sx={saveBtnSx}>
                            <SaveIcon sx={{ fontSize: 18 }} />
                            {editData ? "Update" : "Save"}
                        </Button>
                    </Box>
                </form>
            </Box>

            {/* Snackbar */}
            <ReusableSnackbar
                open={!!successMessage}
                onClose={(event, reason) => {
                    if (reason !== 'clickaway') setSuccessMessage('');
                }}
                message={successMessage}
                autoHideDuration={2000}
                vertical="top"
                horizontal="right"
            />
        </Box>
    );
};

export default RMAdd;